import { post } from "aws-amplify/api";
import { stageConfig } from "..";



// Fundamental variables
let wsConnection = null;
let groupPlaySessionId = null;


// Variables to track timeouts and throttling
var openConnectionTimestamps = [];   // this is a list of timestamps indicating when attempts to connect were made: helps determine when to stop retrying
let lastActivityTimestamp;  // this is compared with Date.now() to determine timeouts

// Note that unlike constructor web sockets, there is no throttling mechanism built in.
// This is because during play, events are typically submitted just one at a time (e.g. one letter per keystroke) and it's rare to have bursts of events together.



export async function createNewGroupPlaySession(puzzleId) {
  groupPlaySessionId = null;    // reset any existing group play session

  try {
    const resp = await (await post({
      apiName: 'groupPlaySessions',
      path: '/',
      options: {
        body: {
          puzzleId,
        },
      },
    }).response).body.json();

    return resp.groupPlaySessionId;
  } catch (e) {
    console.log('Could not create a group play session', e);
    return null;
  }
}



export async function joinGroupPlaySession(groupPlaySessionIdToJoin, callbacks, options) {
  groupPlaySessionId = groupPlaySessionIdToJoin;

  const {
    handleReceivedUpdate,
    onError,
  } = callbacks;
  let {
    displayName = null,
    cursorColor = null,
    playEventHistory = [],
    id = -1,
  } = options;

  playEventHistory = playEventHistory || [];

  wsConnection = new WebSocket(stageConfig.webSocket.groupPlaySessionUrl);
  lastActivityTimestamp = Date.now();
  openConnectionTimestamps.push(lastActivityTimestamp);

  // When the connection opens, send a message to signal you want to join a group play session
  wsConnection.onopen = () => {
    try {
      wsConnection.send(JSON.stringify({
        action: 'submitEvents',
        data: {
          groupPlaySessionId,
          events: [
            { eventType: 'join', eventBody: { displayName, cursorColor, playEventHistory, id } },
          ],
        }
      }));
    } catch (e) {
      // If the websocket is tried to open multiple times in quick succession, this can lead to (harmless in production) errors if onopen is called after the object is no longer usable
      console.log(e);
    }
  };

  // Log any errors, for debugging purposes
  wsConnection.onerror = e => {
    console.log('websocket.onerror:', e);
  }

  // If the web socket connection closes, diagnose why it closed (because of an error?) and handle appropriately. This is more reliable than using the onerror handler.
  wsConnection.onclose = e => {
    console.log('Handling a websocket close with event:', e);

    if (e.code === 3456) {
      // This was an intentional closure called by the customer, so don't do anything. The other cases thus signify errors and unintentional closures.
    } else if (e.reason === 'Going away' || lastActivityTimestamp < Date.now() - 8 * 60 * 1000) {    // API Gateway gives this reason on their 10 minute timeout
      // If the last user activity was over 8 minutes ago, this counts as a timeout error - so terminate and leave up to BoardInteractionContext about when it wants to reinitiate
      onError({ cause: 'timeout' });
    } else if (openConnectionTimestamps.length > 3 && openConnectionTimestamps[openConnectionTimestamps.length - 3] > Date.now() - 10000) {
      // If we've tried to (re)connect 3+ times in the last 10 seconds, there's something wrong, so we just terminate by calling onError()
      onError({ cause: 'error' });
    } else {
      // Otherwise, we'll try to reconnect by creating a new websocket connection
      joinGroupPlaySession(groupPlaySessionId, callbacks, options);
    }
  }

  // Handle any received message
  wsConnection.onmessage = e => {
    const { message, payload } = JSON.parse(e.data);

    if (message === 'groupPlaySessionUpdate') {
      handleReceivedUpdate(payload);

    } else {
      console.log('Warning: message not handled by client', e.data);
      if (wsConnection) wsConnection.close(3011, 'Unhandled message to client');
    }
  }
}


/**
 * Called by customers to close the web socket connection. Closes with custom error code 3456 to signify we should not try to reopen.
 */
export function leaveGroupPlaySession() {
  if (wsConnection) wsConnection.close(3456, 'User requested');
  console.log('Connection closed.');
}




let eventBuffer = [];
let flushTimeout = null;

/**
 * Submits a group of events (batched within 50 ms)
 * @param {{ eventType, eventBody }[]} events 
 */
export function submitEvents(events) {
  eventBuffer.push(...events);

  if (!flushTimeout) {
    flushTimeout = setTimeout(() => {
      try {
        wsConnection.send(JSON.stringify({
          action: 'submitEvents',
          data: {
            groupPlaySessionId,
            events: eventBuffer,
          },
        }));
      } catch (e) {
        console.log(e);
      } finally {
        eventBuffer = [];
        flushTimeout = null;
      }
    }, 50);
  }
}

/**
 * Submits a single event
 * @param {string} eventType 
 * @param {*} eventBody 
 */
export function submitEvent(eventType, eventBody) {
  submitEvents([ {eventType, eventBody } ]);
}






